import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { updateProfile } from "../../../api/UpdateProfile";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faPen } from "@fortawesome/free-solid-svg-icons";

function Profile() {
  const [profileFormEnabled, setProfileFormEnabled] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    phone: "",
  });
  const [localUserData, setLocalUserData] = useState(null);
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const userData = useSelector((state) => state.userData.userData);

  useEffect(() => {
    try {
      if (Object.keys(userData).length !== 0) {
        const parsedUserData = JSON.parse(userData);
        setLocalUserData(parsedUserData);
        setFormData({
          first_name: parsedUserData.first_name,
          last_name: parsedUserData.last_name,
          username: parsedUserData.email,
          email: parsedUserData.email,
          phone: parsedUserData.phone,
        });
      } else {
        throw new Error("Error parsing data");
      }
    } catch (error) {
      console.error("Failed to parse user data: ", error);
      localStorage.removeItem("userData");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessToken");
      navigate("/");
    }
  }, [navigate, userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    try {
      if (localUserData) {
        await updateProfile(localUserData.id, formData);
        setUpdateSuccess("Updated successfully!");
        setProfileFormEnabled(false);
        setTimeout(() => setUpdateSuccess(""), 3000);
      }
    } catch (error) {
      setUpdateSuccess("");
      const errorMessage = errorHandler(error);
      setErrorMsg(errorMessage);
      setTimeout(() => setErrorMsg(""), 3000);
    }
  };

  return (
    <div className="flex-1 bg-gray-100 min-h-screen p-8">
      <h1 className="!text-[30px] !font-bold !font-poppins text-blue-navy text-left mb-6">User Profile</h1>

      <div className="bg-white rounded-2xl shadow-md p-8">
        {/* Card Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="!text-xl !font-bold text-gray-900">User Profile</h2>
          <div className="flex gap-3">
            <button
              type="submit"
              form="profile-form"
              disabled={!profileFormEnabled}
              className="flex items-center gap-2 bg-blue-navy text-white px-5 py-2 rounded-full text-sm font-semibold hover:opacity-90 disabled:opacity-40"
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
              Save
            </button>
            <button
              type="button"
              onClick={() => setProfileFormEnabled(true)}
              className="flex items-center gap-2 bg-blue text-white px-5 py-2 rounded-full text-sm font-semibold hover:opacity-90"
            >
              <FontAwesomeIcon icon={faPen} />
              Edit
            </button>
          </div>
        </div>

        {/* Form */}
        <form id="profile-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            <div className="flex flex-col gap-1">
              <label className="!text-sm !font-semibold text-gray-800">First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={!profileFormEnabled}
                placeholder="Enter First Name"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none disabled:bg-white"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="!text-sm !font-semibold text-gray-800">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={!profileFormEnabled}
                placeholder="Add Last Name"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none disabled:bg-white"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="!text-sm !font-semibold text-gray-800">Email/Username</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!profileFormEnabled}
                placeholder="Add Email/Username"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none disabled:bg-white"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="!text-sm !font-semibold text-gray-800">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!profileFormEnabled}
                placeholder="Add Phone"
                className="rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 placeholder-gray-400 outline-none disabled:bg-white"
              />
            </div>
          </div>

          {(updateSuccess || errorMsg) && (
            <div className="mt-4 text-center">
              {updateSuccess && <p className="text-green-500 text-sm">{updateSuccess}</p>}
              {errorMsg && <p className="text-red-500 text-sm">{errorMsg}</p>}
            </div>
          )}
        </form>
      </div>

    </div>
  );
}

export default Profile;
